import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { AuthContext } from '../context/AuthContext';
import { Button, Alert, Row, Col } from 'react-bootstrap';
import { ExclamationTriangle } from 'react-bootstrap-icons';
import SoilMoistureCalibrationModal from '../components/device/SoilMoistureCalibrationModal';
import DeviceHistoryChart from '../components/device/DeviceHistoryChart';
import MetricCard, { getTrend } from '../components/ui/MetricCard';
import StatusBadge from '../components/ui/StatusBadge';
import { DeviceDashboardSkeleton } from '../components/ui/Skeleton';
import { useToast } from '../context/ToastContext';
import { getSensorMqttKey } from '../utils/deviceIds';

const socket = io(process.env.REACT_APP_API_URL || '');

const DeviceDashboard = () => {
  const { deviceId } = useParams();
  const [deviceData, setDeviceData] = useState([]);
  const [device, setDevice] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const { token } = useContext(AuthContext);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0);
  const previousReadingsRef = useRef({});
  const location = useLocation();
  const [expandedSensor, setExpandedSensor] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('live');
  const getDefaultCustomDates = () => {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    // Use local date methods to ensure proper timezone handling
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    return {
      start: formatLocalDate(weekAgo),
      end: formatLocalDate(today)
    };
  };
  const [customStartDate, setCustomStartDate] = useState(getDefaultCustomDates().start);
  const [customEndDate, setCustomEndDate] = useState(getDefaultCustomDates().end);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [calibratingSensor, setCalibratingSensor] = useState(null);
  const [calibrationValues, setCalibrationValues] = useState({ min: 900, max: 2400 });
  const [sliderValues, setSliderValues] = useState({ min: 900, max: 2400 });
  const [loadingCalibrationValues, setLoadingCalibrationValues] = useState(false);
  const [currentRawReading, setCurrentRawReading] = useState(2048);
  const previousSafeguardsStateRef = useRef({});

  const deviceName = device?.deviceName || location.state?.deviceName || `ID ${deviceId}`;

  // Fetch device info
  useEffect(() => {
    const fetchDevice = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const deviceData = response.data.deviceData[0];
        setDevice(deviceData);
        setDeviceData([{
          data: deviceData.data || {},
          timestamp: deviceData.timestamp,
          _id: deviceData._id
        }]);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch device data');
      }
    };

    fetchDevice();
  }, [deviceId, token]);

  // Detect safeguards state changes for feedback
  useEffect(() => {
    if (!device?.actionPins || !Array.isArray(device.actionPins)) return;

    device.actionPins.forEach(pin => {
      if (!pin) return; // Skip null/undefined pins

      // If safeguards were active and now they're not, show success feedback
      // Note: We now use optimistic updates, so this alert is disabled to avoid double alerts
      // if (previousSafeguardsActive && !currentSafeguardsActive && pin.type === 'water_pump') {
      //   alert('Safeguards reset successfully!');
      // }
    });

    // Update previous state
    const newPreviousState = {};
    device.actionPins.forEach(pin => {
      if (!pin) return; // Skip null/undefined pins

      const pinId = pin.actionPinID || pin.pin;
      newPreviousState[pinId] = pin.safeguardsActive === true;
    });
    previousSafeguardsStateRef.current = newPreviousState;
  }, [device?.actionPins]);

  // Fetch history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        let url = `${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}/history?period=${selectedPeriod}`;

        if (selectedPeriod === 'custom' && customStartDate && customEndDate) {
          const timezoneOffset = new Date().getTimezoneOffset();
          url += `&startDate=${customStartDate}&endDate=${customEndDate}&timezoneOffset=${timezoneOffset}`;
        }

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(response.data.history);
      } catch (err) {
        console.error('Failed to fetch history', err);
      }
    };

    fetchHistory();
  }, [deviceId, token, selectedPeriod, customStartDate, customEndDate]);

  // WebSocket live updates
  useEffect(() => {
    socket.emit('authenticate', token);

    socket.on('newDeviceData', (newDevice) => {
      if (Number(newDevice.deviceId) !== Number(deviceId)) return;

      // Check for sensor data response (from get_sensor_data command)
      if (newDevice.data && newDevice.data.sensor_data_response) {
        const pin = String(newDevice.data.pin);
        const newCalibrationValues = {
          min: newDevice.data.wetValue || 900,
          max: newDevice.data.dryValue || 2400
        };
        setCalibrationValues(newCalibrationValues);
        // Also update slider values if modal is open for this sensor
        if (calibratingSensor && String(calibratingSensor.pin) === pin) {
          setSliderValues(newCalibrationValues);
        }
        setCurrentRawReading(newDevice.data.rawReading || 1800);
        return; // Don't add to device data
      }

      // Check for raw reading response (from get_raw_reading command)
      if (newDevice.data && newDevice.data.raw_reading_response) {
        setCurrentRawReading(newDevice.data.rawReading || 1800);
        return; // Don't add to device data
      }

      // Check if this update includes email notification state changes
      if (newDevice.data && newDevice.data.emailNotifications) {
        setDevice(prev => ({
          ...prev,
          emailNotifications: newDevice.data.emailNotifications
        }));
        // Remove emailNotifications from sensor data before processing
        const sensorData = { ...newDevice.data };
        delete sensorData.emailNotifications;
        newDevice.data = sensorData;
      }

      // Update action pins state if the device object includes updated actionPins
      if (newDevice.actionPins) {
        setDevice(prev => ({
          ...prev,
          actionPins: [...newDevice.actionPins] // Create a copy to ensure React detects changes
        }));
      }

      const normalized = {
        data: newDevice.data || {},
        timestamp: newDevice.timestamp,
        _id: newDevice._id
      };

      setDeviceData(prev => {
        const filtered = prev.filter(d => d._id !== normalized._id);
        return [normalized, ...filtered].slice(0, 50);
      });

      // Update history for live view
      if (selectedPeriod === 'live') {
        setHistory(prev => {
          const newEntry = { data: normalized.data, timestamp: normalized.timestamp };
          const updated = [newEntry, ...prev];
          return updated.slice(0, 60);
        });
      }
    });

    return () => {
      socket.off('newDeviceData');
    };
  }, [deviceId, token, selectedPeriod, calibratingSensor]);



  useEffect(() => {
    const ts = deviceData[0]?.timestamp;
    if (!ts) return undefined;
    const tick = () => setSecondsSinceUpdate(Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deviceData]);

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!device) return <DeviceDashboardSkeleton />;



  const currentData = deviceData[0]?.data || {};

  // Helper function to get the field name used in MQTT data, matching sketch generation logic
  const getFieldName = (sensor) => getSensorMqttKey(sensor);

  const openCalibrationModal = async (sensor) => {
    setCalibratingSensor(sensor);
    setShowCalibrationModal(true);
    setLoadingCalibrationValues(true);

    // Fetch current EEPROM values
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}/calibration/${sensor.pin}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentValues = {
        min: response.data.wetValue || 1800,
        max: response.data.dryValue || 4095
      };
      setCalibrationValues(currentValues);
      setSliderValues(currentValues); // Initialize sliders to current values
    } catch (err) {
      console.error('Failed to fetch calibration values:', err);
      // Use defaults if fetch fails
      const defaultValues = { min: 1800, max: 4095 };
      setCalibrationValues(defaultValues);
      setSliderValues(defaultValues);
    } finally {
      setLoadingCalibrationValues(false);
    }
  };



  const refreshSensorReading = async () => {
    if (!calibratingSensor) return;

    try {
      setLoadingCalibrationValues(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}/raw-reading`, {
        pin: calibratingSensor.pin,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentRawReading(response.data.rawReading ?? 1800);
    } catch (err) {
      console.error('Failed to refresh sensor reading:', err);
    } finally {
      setLoadingCalibrationValues(false);
    }
  };

  const saveManualCalibration = async () => {
    if (!calibratingSensor) return;

    try {
      await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}/calibration/${calibratingSensor.pin}`, {
        wetValue: sliderValues.min,
        dryValue: sliderValues.max
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowCalibrationModal(false);
      toast.success('Calibration values saved successfully!');
    } catch (err) {
      toast.error('Failed to save calibration values: ' + (err.response?.data?.error || err.message));
    }
  };



  const lastTimestamp = deviceData[0]?.timestamp;
  const connected = lastTimestamp && Date.now() - new Date(lastTimestamp).getTime() < 30000;

  return (
    <>
      <div className="app-status-strip mb-4 p-3">
        <div>
          <div className="fw-semibold mb-1" style={{ fontFamily: 'var(--font-display)' }}>{deviceName}</div>
          <div className="app-status-strip__meta">
            <StatusBadge connected={connected} timestamp={lastTimestamp} />
            {lastTimestamp && <span>Updated {secondsSinceUpdate}s ago</span>}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={() => navigate(`/app/devices/edit/${deviceId}`)}>
            Edit Device
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => navigate('/app/dashboard')}>
            Back
          </Button>
        </div>
      </div>

      <Row>
          {device.sensors?.map((sensor, index) => {
            const fieldName = getFieldName(sensor);
            const rawValue = currentData[fieldName];
            const value = typeof rawValue === 'object' ? null : rawValue;
            const prev = previousReadingsRef.current[fieldName];
            if (value != null) previousReadingsRef.current[fieldName] = value;
            const trend = getTrend(value, prev);

            return (
              <Col md={4} key={index} className="mb-4">
                <MetricCard
                  title={`${sensor.name || sensor.sensorType} (Pin ${sensor.pin})`}
                  value={value ?? 'N/A'}
                  sensorType={sensor.sensorType}
                  expanded={expandedSensor === sensor}
                  trend={value != null ? trend : null}
                  onClick={() => setExpandedSensor(expandedSensor === sensor ? null : sensor)}
                >
                    <div className="mt-2 d-flex gap-2">
                      {sensor.sensorType === 'soil_moisture' && (
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCalibrationModal(sensor);
                          }}
                        >
                        Calibrate
                        </Button>
                      )}

                      {/* Alert Reset Button - Only show if email notifications are enabled and there are triggered alerts for this sensor */}
                      {device.emailNotifications?.enabled && device.emailNotifications.alertRules?.some(rule =>
                        rule.enabled && rule.isTriggered && rule.sensor === sensor.sensorID
                      ) && (
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              // Reset all triggered alerts for this sensor
                              const updatedRules = device.emailNotifications.alertRules.map(rule => {
                                if (rule.sensor === sensor.sensorID && rule.isTriggered) {
                                  return { ...rule, isTriggered: false };
                                }
                                return rule;
                              });

                              await axios.put(
                                `${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}`,
                                {
                                  emailNotifications: {
                                    ...device.emailNotifications,
                                    alertRules: updatedRules
                                  }
                                },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );

                              // Update local state
                              setDevice(prev => ({
                                ...prev,
                                emailNotifications: {
                                  ...prev.emailNotifications,
                                  alertRules: updatedRules
                                }
                              }));

                              toast.success('Alerts reset successfully!');
                            } catch (err) {
                              console.error('Failed to reset alerts:', err);
                              toast.error('Failed to reset alerts');
                            }
                          }}
                        >
                          Reset Alerts
                        </Button>
                      )}
                    </div>
                </MetricCard>
              </Col>
            );
          })}
          {device.actionPins?.map((pin, index) => {
            const activeKey = pin.actionPinID;
            const isActive = currentData[activeKey] === true;
            const safeguardsActive = pin.safeguardsActive === true;
            const lastActiveTime = pin.lastActiveTime ? new Date(pin.lastActiveTime) : null;
            const timeSinceActive = lastActiveTime ? new Date() - lastActiveTime : null;

            // Format time display
            let timeDisplay = 'Never';
            if (lastActiveTime) {
              if (timeSinceActive < 60000) { // Less than 1 minute
                timeDisplay = 'Just now';
              } else if (timeSinceActive < 3600000) { // Less than 1 hour
                const minutes = Math.floor(timeSinceActive / 60000);
                timeDisplay = `${minutes} min ago`;
              } else if (timeSinceActive < 86400000) { // Less than 1 day
                const hours = Math.floor(timeSinceActive / 3600000);
                timeDisplay = `${hours} hr ago`;
              } else {
                timeDisplay = lastActiveTime.toLocaleDateString();
              }
            }

            return (
              <Col md={4} key={`action-${index}`} className="mb-4">
                <MetricCard
                  title={`${pin.name || pin.type} (Pin ${pin.pin})`}
                  value={isActive ? 'ON' : 'OFF'}
                  variant="digital"
                >
                  <div className="actuator-indicator mt-2">
                    <div className={`actuator-indicator__switch ${isActive ? 'actuator-indicator__switch--on' : ''}`}>
                      <div className="actuator-indicator__knob" />
                    </div>
                    <span>{isActive ? 'Active' : 'Off'}</span>
                  </div>
                  <div className="mt-2 opacity-75" style={{ fontSize: '0.85rem' }}>
                    Last active: {timeDisplay}
                  </div>
                  {safeguardsActive && pin.type === 'water_pump' && (
                    <div className="safeguard-banner d-flex align-items-center gap-1">
                      <ExclamationTriangle size={14} />
                      <span>Safeguards active — pump shut down</span>
                    </div>
                  )}
                  {safeguardsActive && pin.type === 'water_pump' && (
                    <div className="mt-2">
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={async () => {
                          try {
                            await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}/reset-safeguards`, {}, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            setDevice((prev) => ({
                              ...prev,
                              actionPins: prev.actionPins?.map((p) =>
                                p.type === 'water_pump' ? { ...p, safeguardsActive: false } : p
                              ),
                            }));
                            toast.success('Safeguards reset command sent successfully!');
                          } catch (err) {
                            console.error('Failed to reset safeguards:', err);
                            toast.error('Failed to reset safeguards');
                          }
                        }}
                      >
                        Reset Safeguards
                      </Button>
                    </div>
                  )}
                </MetricCard>
              </Col>
            );
          })}
        </Row>



        <DeviceHistoryChart
          expandedSensor={expandedSensor}
          cardVariant="sensor"
          history={history}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          setCustomStartDate={setCustomStartDate}
          setCustomEndDate={setCustomEndDate}
          device={device}
          getFieldName={getFieldName}
          getDefaultCustomDates={getDefaultCustomDates}
        />

        <SoilMoistureCalibrationModal
          show={showCalibrationModal}
          onHide={() => setShowCalibrationModal(false)}
          sensor={calibratingSensor}
          loading={loadingCalibrationValues}
          calibrationValues={calibrationValues}
          sliderValues={sliderValues}
          currentRawReading={currentRawReading}
          onSliderChange={setSliderValues}
          onRefresh={refreshSensorReading}
          onSave={saveManualCalibration}
        />
    </>
  );
};

export default DeviceDashboard;
